import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditApplication } from '_gql'

import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { DSA_VERSION_TYPES } from '@sputnik/pages/ReviewAndSign/utils'
import { PATHNAME_CONSTS, PRODUCT_CONST_STRINGS } from '@sputnik/resources/constants'
import { logInfo } from '@sputnik/utils/loggerUtils'

interface ReviewMigrationBannerProps {
  dsaVersion: CreditApplication['dsaVersion']
  salesforceCreditId: string
}

// this banner shows for needing to review and resubmit a migration application
const ReviewMigrationBanner = ({ salesforceCreditId, dsaVersion }: ReviewMigrationBannerProps) => {
  const navigate = useNavigate()
  const migrationType =
    dsaVersion === DSA_VERSION_TYPES.CRB_MC_B_O || dsaVersion === DSA_VERSION_TYPES.CRB_MC_J_S ? 'Mastercard' : 'Visa'

  const { isAppVersion3, isLoadingCreditAppTypes } = useGetCreditAppTypes({
    salesforceCreditId: salesforceCreditId || '',
  })

  const onReviewApplicationClick = () => {
    const navigatePath = isAppVersion3
      ? `/app/${salesforceCreditId}/${PATHNAME_CONSTS.REVIEW_PATH}`
      : `/app/${salesforceCreditId}/section/${PATHNAME_CONSTS.REVIEW_AND_SIGN_PATH}`

    logInfo('onReviewApplicationClick', {
      attributes: {
        creditAppId: salesforceCreditId,
        result: `Clicked review ${migrationType} application link from banner and navigated to ${navigatePath}`,
      },
      eventName: 'onReviewApplicationClick',
    })

    navigate(navigatePath)
  }

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Informative}
        subtext={
          <FormattedMessage
            defaultMessage='As requested, we’re migrating your {BILL_SPEND_AND_EXPENSE} account to {migrationType}. <link>Review and re-submit your application</link>'
            id='sputnik.ReviewMigrationBanner__dezVS5'
            values={{
              BILL_SPEND_AND_EXPENSE: PRODUCT_CONST_STRINGS.BILL_SPEND_AND_EXPENSE,
              migrationType: migrationType,
              link: (chunks) => (
                <TextLink
                  disabled={isLoadingCreditAppTypes}
                  size={TextLinkSizes.SMALL}
                  onClick={onReviewApplicationClick}
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        }
        title={
          <FormattedMessage
            defaultMessage='Review your {migrationType} application'
            id='sputnik.ReviewMigrationBanner__1JRfgv'
            values={{
              migrationType: migrationType,
            }}
          />
        }
      />
    </div>
  )
}

export default ReviewMigrationBanner
